// exports ArrayProperty, isNativeArray, isArray, toArray, cloneDeep

import { assign, indexOf, lastIndexOf, forEach, forEachRight, every, some,
         map, reduce, reduceRight, filter, find, findLast } from 'lodash';

import { OwnedObject, cloneDeep, isArray, concat } from './base';
import { areEqual } from './util';

// This is the best we could do in ES5
// An ArrayProperty is an object with keys '0', '1', '2'... and a synced length
// It works like an array-like object and include extra functionality
// Collection utilies from lo-dash works on them (filter, find, map, etc, clone)
// Other functions from lo-dash will not work as expected (cloneDeep, flatten)
// Use instead cloneDeep, flatten, concat, isArray from Grafika utilities
// In ES2015 it is possible to extends the native array. We are going to migrate
// to a better implementation once browsers implements support for this feature.

const aProto = Array.prototype;

export const ArrayProperty = OwnedObject.extend({ typeName: 'ArrayProperty',

  __ArrayLike: true,
  __CxArray: true,

  get isEmpty() { return this.length === 0; },
  get isArray() { return true; },

  // config: { onChange, constructItem, releaseItem }
  init( array,owner,config ) {
    assign(this,config);
    this.length = 0;
    this.owner( owner || null );
    this.initArrayProperty();
    this._add( array || [] ); // this should be the last line
  },

  initArrayProperty() { },

  onChange    () {},
  constructItem ( c ) { return c; },
  releaseItem   ( /*c*/) {},

  size  ()    { return this.length; },
  toArray ( s,e) { return s===undefined ? aProto.slice.call(this,0) : ( e===undefined ? aProto.slice.call(this,s) : aProto.slice.call(this,s,e) ); },

  at( i,v) {
    if( v===undefined ) {
      return this[i];
    }
    this.replaceAt(i,v);
  },

  // Cx extensions

  textualize( separator = ',' ) {
    const { length } = this;
    if( length === 0 ) {
      return '';
    }

    let t, k;

    if( this[0].textualize ) {
      t = this[0].textualize();
      for(k=1;k<length;++k) {
        t += separator + this[k].textualize();
      }
    }
    else {
      t = this[0];
      for(k=1;k<length;++k) {
        t += separator + this[k];
      }

    }
    return t;
  },
  contains( v ) {
    return this.indexOf(v)!==-1;
  },

  clone( deep ) {
    if( deep === true ) {
      return this.cloneDeep();
    }
    const a = [];
    for(let k=0,kEnd=this.length;k<kEnd;++k) {
      a[k] = this[k];
    }
    return new ArrayProperty(a);
  },

  cloneDeep() {
    const a = [];
    for(let k=0,kEnd=this.length;k<kEnd;++k) {
      a[k] = cloneDeep(this[k]);
    }
    return new ArrayProperty(a);
  },

  isEqual( other ) {
    if( this.length !== other.length ) {
      return false;
    }
    for( let i = 0, iEnd = this.length; i<iEnd; ++i ) {
      if( ! areEqual(this[i],other[i]) ) {
        return false;
      }
    }
    return true;
  },

  clear() {
    if( this.length === 0 ) {
      return [];
    }
    const e = this._clear();
    this.onChange.call(this._owner,null,e);
    return e;
  },
  add( v ) {
    const a = this._add(v);
    this.onChange.call(this._owner,a);
    return a;
  },
  set( v ) {
    if( v.length === 0 && this.length === 0 ) {
      return [];
    }
    const e = this._set(v);
    this.onChange.call(this._owner,e.added,e);
    return e;
  },
  remove( v ) {
    const e = this._remove(v);
    this.onChange.call(this._owner,null,e);
    return e;
  },
  erase( i, h ) {
    const e = this._erase(i,h);
    this.onChange.call(this._owner,null,e);
    return e;
  },
  insert( i, v ) {
    const a = this._insert(i,v);
    this.onChange.call(this._owner,a);
    return a;
  },
  replace( o, v ) {
    const e = this._replace(o,v);
    this.onChange.call(this._owner,e.added,e);
    return e;
  },
  replaceAt( i, v ) {
    const e = this._replaceAt(i,v);
    this.onChange.call(this._owner,e.added,[e]);
    return e;
  },
  moveForward( v, n ) {
    this._moveForward(v,n);
    this.onChange.call(this._owner);
  },
  moveBackward( v, n ) {
    this._moveBackward(v,n);
    this.onChange.call(this._owner);
  },

  // Mutator

  pop() {
    const r = aProto.pop.call(this);
    this._releaseItem(r);
    this.onChange.call(this._owner,null,[r]);
    return r;
  },
  shift() {
    const r = aProto.shift.call(this);
    this._releaseItem(r);
    this.onChange.call(this._owner,null,[r]);
    return r;
  },
  push( v ) {
    const a = this._push(v);
    this.onChange.call(this._owner,[a]);
  },
  unshift( v ) {
    const a = this._unshift(v);
    this.onChange.call(this._owner,[a]);
  },
  sort( f ) {
    aProto.sort.call(this,f);
    this.onChange.call(this._owner);
  },
  reverse() {
    aProto.reverse.call(this);
    this.onChange.call(this._owner);
  },
  splice() {
    const e = this._splice.apply(this,arguments);
    this.onChange.call(this._owner,e.added,e);
    return e;
  },

// Accesor

  concat( a ) {
    return concat(this,a);
  },
  join( c ) {
    return aProto.join.call(this,c);
  },
  slice() {
    return Array.prototype.slice.apply(this,arguments);
  },
  toString() {
    return this.slice(0).toString();
  },
  indexOf( v ) {
    return indexOf(this,v);
  },
  lastIndexOf( v ) {
    return lastIndexOf(this,v);
  },

// Iteration
  forEach( f ) {
    return forEach(this,f);
  },

  every( f ) {
    return every(this,f);
  },

  some( f ) {
    return some(this,f);
  },

  filter( f ) {
    return filter(this,f);
  },

  find( f ) {
    return find(this,f);
  },

  map( f ) {
    return map(this,f);
  },

  reduce( f, i ) {
    return reduce(this,f,i);
  },

  reduceRight( f, i ) {
    return reduceRight(this,f,i);
  },

  forEachRight( f ) {
    return forEachRight(this,f);
  },

  findLast( f ) {
    return findLast(this,f);
  },

// Internals

  _releaseItem( v ) {
    this.releaseItem.call(this._owner,v);
    return v;
  },

  _adoptItem( v ) {
    return this.constructItem.call(this._owner,v);
  },

  _adopt( v ) {
    if( isArray(v) ) {
      const a = [];
      for(let k=0,kEnd=v.length;k<kEnd;++k) {
        const nv_k = this._adoptItem( v[k] );
        // if( nv !== null ) // [TODO] use AP.{}
        a.push(nv_k);
      }
      return a;
    }
    else {
      const nv = this._adoptItem(v);
      return [nv]; //  nv !== null ? [nv] : [] // [TODO] use AP.{}
    }
  },

  _release( v ) {
    for( let k=0, kEnd = v.length; k < kEnd; ++k ) {
      this._releaseItem(v[k]);
    }
    return v;
  },

  _at( i,v) {
    if( v===undefined ) {
      return this[i];
    }
    else{
      return this._replaceAt(i,v);
    }
  },
  _push( v ) {
    const a = this._adoptItem(v);
    aProto.push.call(this,a);
    return a;
  },
  _unshift( v ) {
    const a = this._adoptItem(v);
    aProto.unshift.call(this,a);
    return a;
  },
  _clear() {
    if( this.length === 0 ) {
      return [];
    }
    return this._release( aProto.splice.call(this,0,this.length) );
  },
  _set( v ) {
    if( this.length === 0 && v.length === 0 ) {
      const ee = []; ee.added = []; return ee;
    }
    const e = this._release( aProto.splice.call(this,0,this.length) );
    e.added = this._simpleSplice(0,0,v).added;
    return e;
  },
  _add( v ) {
    const e = this._simpleSplice(this.length,0,v);
    return e.added;
  },
  _insert( i,v) {
    const e = this._simpleSplice(i,0,v);
    return e.added;
  },
  _erase( i, h ) {
    return this._release( aProto.splice.call(this,i, h===undefined? 1 : h ) );
  },
  _remove( v ) {
    if( v === this ) {
      return this._clear();
    }
    const r = [];
    const l = this;
    const tryToRemove = function(e) {
      const i = indexOf(l,e);
      if( i !== -1 ) {
        r.push( aProto.splice.call(l,i,1)[0] );
      }
    };
    if( isArray(v) ) {
      //_.forEach(v,tryToRemove);
      for(let k=0,kEnd=v.length;k<kEnd;++k) {
        tryToRemove(v[k]);
      }
    }
    else {
      tryToRemove(v);
    }
    return this._release(r);
  },
  _replace( o, v ) {
    const r = o === this ? this._clear() : [];

    const l = this;
    let ii = l.length;
    const tryToRemove = function(e) {
      const i = indexOf(l,e);
      if(i!==-1) {
        r.push( aProto.splice.call(l,i,1)[0] );
        if( i < ii ) {
          ii = i;
        }
      }
    };
    if( isArray(o) ) {
      //_.forEach(o,tryToRemove);
      for(let k=0,kEnd=o.length;k<kEnd;++k) {
        tryToRemove(o[k]);
      }
    }
    else {
      tryToRemove(o);
    }
    r.added = this._insert(ii,v);
    return this._release(r);
  },
  _replaceAt( i, v ) {
    const nv = this._adoptItem(v);
    const e = this[i] ;
    this[i] = nv;
    if( this.length <= i ) {
      this.length = i+1;
    }

    this._releaseItem(e);

    const r = [ e ];
    r.added = [nv];
    return r;
  },
  _moveForward( v, n ) {
    const moveRange = function(array,s,e,nn) {
      aProto.splice.apply(array,[s+nn,0].concat( aProto.splice.call(array,s,e-s) ));
    };
    const l = this;
    const el = map( l, () => null );
    const tryToSearch = function(e) {
      const i = indexOf(l,e);
      if( i !== -1 ) {
        el[i] = e;
      }
    };
    if( isArray(v) ) {
      if( v === l ) {
        v = v.clone();
      }
      //_.forEach(v,tryToSearch);
      for( let k=0,kEnd=v.length;k<kEnd;++k) {
        tryToSearch(v[k]);
      }
    }
    else {
      tryToSearch(v);
    }
    if( n > 0 ) {
      let lend = l.length;
      for( let k = el.length-1; k >= 0 ; --k ) {
        if( el[k] !== null ) {
          const end = k+1;
          while( k >= 0 && el[k] !== null ) {
            --k;
          }
          const start = k+1;
          const nTilEnd = Math.min( n, lend-end );
          moveRange( l, start, end, nTilEnd );
          lend = start+nTilEnd;
        }
      }
    }
    else {
      let lstart = 0;
      for( let k=0, len=el.length; k<len ; ++k ) {
        if( el[k] !== null ) {
          let start = k;
          while( k<len && el[k] !== null ) {
            ++k ;
          }
          let nTilEnd = - Math.min( -n, start-lstart );
          moveRange( l, start, k, nTilEnd );
          lstart = k+nTilEnd;
        }
      }
    }
  },
  _moveBackward( v, n ) {
    return this._moveForward(v,-n);
  },
  _simpleSplice( i, h, v ) {
    if(v === undefined) {
      let e = aProto.splice.call(this,i,h);
      e.added = [];
      return this._release(e);
    }
    else {
      if( isArray(v) ) {
        let anv = this._adopt(v);
        let e = aProto.splice.apply(this,[i,h].concat(anv));
        e.added = anv;
        return this._release(e);
      }
      else {
        let nv = this._adoptItem(v);
        let e = aProto.splice.call(this,i,h,nv);
        e.added = [nv];
        return this._release(e);
      }
    }
  },
  _splice( i, h ) {
    if( arguments.length === 2 ) {
      return this._simpleSplice(i,h);
    }
    else
    {
      let va = [];
      for(let na=2, len = arguments.length; na<len; ++na ) {
        const v = arguments[na];
        if( isArray(v) ) {
          va = concat(va,v);
        }
        else {
          va.push(v);
        }
      }
      return this._simpleSplice(i,h,va);
    }
  }
});

OwnedObject.Type.ArrayProperty = ArrayProperty;



/** WEBPACK FOOTER **
 ** ./src/gear/array.js
 **/